import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * In-app replacement for window.confirm / window.alert.
 *
 * The native dialogs were wrong here for three reasons: they are unstyled and
 * look nothing like the rest of the app, they block the JS thread so nothing
 * behind them can update, and Chrome suppresses them outright after a few in a
 * row - so a confirmation the code depends on can silently never appear and the
 * call returns false. Several destructive actions were gated on exactly that.
 *
 * The API is promise-based and otherwise shaped like the ones it replaces, so a
 * call site changes from
 *     if (!window.confirm(msg)) return;
 * to
 *     if (!await confirm(msg)) return;
 * and nothing else about the surrounding logic moves.
 *
 * Usage:
 *   const { confirm, alert } = useDialog();
 *   if (await confirm('Delete this draft?', { danger: true })) { ... }
 */

const DialogContext = createContext(null);

/**
 * confirm/alert from anywhere under the provider.
 *
 * Returns working no-op-ish fallbacks when used outside a provider rather than
 * throwing: a missing provider should not take down a page, and in tests the
 * component under test is usually mounted bare.
 */
export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (ctx) return ctx;
  return {
    confirm: async (message) => window.confirm(message),
    alert: async (message) => { window.alert(message); },
  };
};

const TONES = {
  danger: {
    accent: 'bg-red-100 text-red-700',
    confirmBtn: 'bg-red-600 hover:bg-red-700',
    // Plain typographic marks, matching the info and success tones below.
    // These were emoji; the colour of the accent already carries the severity.
    icon: '!',
  },
  warning: {
    accent: 'bg-amber-100 text-amber-700',
    confirmBtn: 'bg-amber-600 hover:bg-amber-700',
    icon: '!',
  },
  info: {
    accent: 'bg-blue-100 text-blue-700',
    confirmBtn: 'bg-stanford-red hover:bg-stanford-light-red',
    icon: 'ℹ',
  },
  success: {
    accent: 'bg-emerald-100 text-emerald-700',
    confirmBtn: 'bg-emerald-600 hover:bg-emerald-700',
    icon: '✓',
  },
};

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  // The promise's resolve, held across renders so the buttons can settle it.
  const resolverRef = useRef(null);
  const confirmBtnRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    if (resolve) resolve(value);
  }, []);

  const open = useCallback((options) => new Promise((resolve) => {
    // A second dialog opening while one is up would strand the first promise
    // forever and hang whatever awaited it. Settle it first.
    if (resolverRef.current) resolverRef.current(false);
    resolverRef.current = resolve;
    setDialog(options);
  }), []);

  const confirm = useCallback((message, options = {}) => open({
    kind: 'confirm',
    message,
    title: options.title || 'Please confirm',
    confirmLabel: options.confirmLabel || 'Confirm',
    cancelLabel: options.cancelLabel || 'Cancel',
    tone: options.tone || (options.danger ? 'danger' : 'warning'),
    detail: options.detail || null,
  }), [open]);

  const alert = useCallback((message, options = {}) => open({
    kind: 'alert',
    message,
    title: options.title || 'Notice',
    confirmLabel: options.confirmLabel || 'OK',
    tone: options.tone || 'info',
    detail: options.detail || null,
  }), [open]);

  // Esc cancels, Enter accepts - the keyboard behaviour of the native dialogs
  // these replace, which people have in their fingers.
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); settle(false); }
      if (e.key === 'Enter') { e.preventDefault(); settle(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, settle]);

  // Focus the action so the dialog is usable without reaching for the mouse.
  useEffect(() => {
    if (dialog) confirmBtnRef.current?.focus();
  }, [dialog]);

  const tone = TONES[dialog?.tone] || TONES.info;

  // Memoised: without this the value is a fresh object on every render of this
  // provider - which happens each time a dialog opens or closes - so every
  // consumer would re-render too, including the 2,600-line claim forms that
  // only ever wanted a confirm box.
  const api = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <DialogContext.Provider value={api}>
      {children}

      {dialog && (
        <div
          // Above every other overlay in the app (the review walkthrough sits at
          // 9400): a confirmation raised from inside one has to be reachable.
          className="fixed inset-0 z-[10500] bg-black/50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-dialog-title"
          onMouseDown={(e) => {
            // Backdrop click cancels a confirm, dismisses an alert. Mousedown
            // rather than click so a drag that ends on the backdrop does not
            // count as a dismissal.
            if (e.target === e.currentTarget) settle(dialog.kind === 'alert');
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[calc(100dvh-1.5rem)] sm:block sm:max-h-none">
            <div className="px-5 sm:px-6 py-4 flex items-start gap-3 border-b border-gray-200">
              <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${tone.accent}`}>
                {tone.icon}
              </span>
              <div className="min-w-0">
                <h3 id="app-dialog-title" className="text-lg font-semibold text-gray-900">
                  {dialog.title}
                </h3>
              </div>
            </div>

            <div className="px-5 sm:px-6 py-5 overflow-y-auto sm:overflow-visible">
              {/* whitespace-pre-line so the multi-line messages carried over
                  from the window.confirm calls keep their line breaks. */}
              <p className="text-sm text-gray-700 whitespace-pre-line break-words">{dialog.message}</p>
              {dialog.detail && (
                <p className="mt-3 text-xs text-gray-500 whitespace-pre-line break-words">{dialog.detail}</p>
              )}
            </div>

            <div className="px-5 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-2">
              {dialog.kind === 'confirm' && (
                <button
                  onClick={() => settle(false)}
                  className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  {dialog.cancelLabel}
                </button>
              )}
              <button
                ref={confirmBtnRef}
                onClick={() => settle(true)}
                className={`min-h-[44px] sm:min-h-0 px-5 py-2 text-sm text-white rounded-lg font-medium transition-colors ${tone.confirmBtn}`}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};

DialogProvider.propTypes = {
  children: PropTypes.node,
};

export default DialogProvider;
